import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from 'goober';

import { getMessageFromErrorResponse } from '@/api/v4/base-fetch';
import { jwtTokenState } from '@/auth/auth.state';
import { Loader } from '@/components/loader/loader';
import { paths } from '@/routes/helpers/paths';
import { useAppRedirect } from '@/routes/hooks/use-app-redirect';
import { getAtomValue } from '@/shared/jotai';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { notify } from '@/ui/snackbar/notify';

import { useCompleteMcpOAuthConnect } from './use-complete-mcp-oauth-connect.mutation';

const hasSavvyJwt = (): boolean => {
  const token = getAtomValue(jwtTokenState);
  return typeof token === 'string' && token.length > 0;
};

export const McpOAuthCallback = () => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { redirectToLogin } = useAppRedirect();
  const { mutateAsync: completeConnect } = useCompleteMcpOAuthConnect();
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      notify({ message: t`mcpOAuth.callbackMissingParams` });
      navigate(`${paths.settings()}?tab=apiAndIntegrations`, { replace: true });
      return;
    }

    if (!hasSavvyJwt()) {
      redirectToLogin({ replace: true });
      return;
    }

    hasStarted.current = true;

    void completeConnect({ code, state })
      .then(() => {
        notify({ message: t`mcpOAuth.callbackSuccess` });
        navigate(`${paths.settings()}?tab=apiAndIntegrations`, {
          replace: true,
        });
      })
      .catch((error: unknown) => {
        notify({
          message:
            getMessageFromErrorResponse(error) ?? t('mcpOAuth.callbackError'),
        });
        navigate(`${paths.settings()}?tab=apiAndIntegrations`, {
          replace: true,
        });
      });
  }, [completeConnect, navigate, redirectToLogin, searchParams, t]);

  return (
    <CallbackContainer alignItems="center" justify="center" fullWidth>
      <Loader />
    </CallbackContainer>
  );
};

const CallbackContainer = styled(Flexbox)`
  min-height: 240px;
`;
